import { useState, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, 
  DialogHeader, DialogTitle, DialogTrigger 
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ScrollableTable, Column, SortConfig } from "@/components/ui/scrollable-table";

import { useCollection } from "@/hooks/firestore";
import { 
  useAddDocument, 
  useUpdateDocument, 
  // useDeleteDocument // No longer used directly for deletion
} from "@/hooks/firestore/operations";
import { usePermissions } from "@/hooks/usePermissions";
import { Brand } from "@/types/firestore";
// import { orderBy } from "firebase/firestore"; // Not used in this file

// Redux imports for deletion dialog
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { DeleteDialog } from "../editor/dialogs/DeleteConfirmDialog";
import { 
    EditableEntityType,
    startDeletionAssessment, 
    assessDeletionImpact, 
    deleteEntity, 
    cancelInteraction,
    selectEditorInteractionState,
    selectSelectedEntity,
    selectDeletionImpact,
    selectEditorOperationLoading,
    selectEditorOperationError
} from "@/store/slices/editorSlice";

export const BrandManager = () => {
  const dispatch = useAppDispatch();
  const { documents: brands, loading: collectionLoading } = useCollection<Brand>('brands');
  
  const { addDocument, loading: adding, error: addError } = useAddDocument<Brand>('brands');
  const { updateDocument, loading: updating, error: updateError } = useUpdateDocument<Brand>('brands');
  // const { deleteDocument, loading: deleting, error: deleteError } = useDeleteDocument('brands'); // Replaced by editorSlice logic

  // Editor slice selectors
  const interactionState = useAppSelector(selectEditorInteractionState);
  const selectedEntity = useAppSelector(selectSelectedEntity);
  const deletionImpact = useAppSelector(selectDeletionImpact);
  const operationLoading = useAppSelector(selectEditorOperationLoading);
  const operationError = useAppSelector(selectEditorOperationError);

  const { hasPermission } = usePermissions();
  const canManage = hasPermission('editor') || hasPermission('admin');
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [newBrand, setNewBrand] = useState<{ name: string, brandSize: 'craft' | 'mass' | 'unknown' }>({ 
    name: "", 
    brandSize: "craft" 
  });
  const [filterBrandSize, setFilterBrandSize] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<string | null>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Filter brands
  const processedBrands = useMemo(() => {
    if (!brands) return [];
    let filtered = brands;

    // Filter by brandSize
    if (filterBrandSize !== null) {
      filtered = filtered.filter(b => b.brandSize === filterBrandSize);
    }

    // Filter by searchTerm (name)
    if (searchTerm) {
      filtered = filtered.filter(b => 
        b.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort - now handled by ScrollableTable
    return filtered;
  }, [brands, filterBrandSize, searchTerm]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleAddBrand = async (): Promise<void> => {
    if (!newBrand.name.trim()) {
      toast.error("Brand name cannot be empty.");
      return;
    }
    try {
      const brandToAdd: Omit<Brand, 'id' | 'createdAt'> = {
        name: newBrand.name.trim(),
        brandSize: newBrand.brandSize
      };
      const newId = await addDocument(brandToAdd);
      if (newId) {
        setNewBrand({ name: "", brandSize: "craft" });
        setIsAddDialogOpen(false);
        toast.success(`Brand "${brandToAdd.name}" added successfully.`);
      } else {
         toast.error(`Failed to add brand: ${addError?.message || 'Unknown error'}`);
      }
    } catch (error) {
       console.error("Error adding brand:", error);
       toast.error("Failed to add brand. See console for details.");
    }
  };

  const handleEditBrand = async (): Promise<void> => {
    if (!editingBrand || !editingBrand.id) return;
    if (!editingBrand.name.trim()) {
       toast.error("Brand name cannot be empty.");
       return;
    }
    
    try {
      const success = await updateDocument(editingBrand.id, {
        name: editingBrand.name.trim(),
        brandSize: editingBrand.brandSize
      });
      
      if (success) {
        setEditingBrand(null);
        toast.success(`Brand "${editingBrand.name}" updated successfully.`);
      } else {
         toast.error(`Failed to update brand: ${updateError?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error updating brand:", error);
      toast.error("Failed to update brand. See console for details.");
    }
  };

  // Updated delete brand handler to use Redux dialog flow
  const handleDeleteBrand = useCallback((brand: Brand) => {
    if (!brand || !brand.id) return;
    dispatch(startDeletionAssessment({ entityType: 'brand', entityId: brand.id }));
    // Brands require impact assessment
    dispatch(assessDeletionImpact({ entityType: 'brand', entityId: brand.id }));
  }, [dispatch]);

  const handleDeletionDialogClose = useCallback(() => {
    dispatch(cancelInteraction());
  }, [dispatch]);

  const handleConfirmBrandDeletion = useCallback(async () => {
    if (selectedEntity && selectedEntity.type === 'brand' && selectedEntity.id) {
      try {
        await dispatch(deleteEntity({ entityType: 'brand', entityId: selectedEntity.id })).unwrap();
        
        if (editingBrand?.id === selectedEntity.id) {
          setEditingBrand(null); 
        }
        // cancelInteraction is called by deleteEntity.fulfilled
      } catch (error: any) {
        // Error handling is now done by the dialog component
        throw error;
      }
    }
  }, [dispatch, selectedEntity, editingBrand]);
  
  // Determine assessment state for the unified dialog
  const getDeletionAssessmentState = useCallback(() => {
    // Check error from deletionImpact first (assessment phase)
    if (deletionImpact.error) return 'error';
    if (deletionImpact.loading === 'pending') return 'loading';
    return 'completed';
  }, [deletionImpact.loading, deletionImpact.error]);

  // Overall loading state, uses operationLoading for deletion now
  const generalLoading = collectionLoading || adding || updating || (operationLoading === 'pending' && interactionState === 'confirming_deletion');

  // Create table columns configuration
  const tableColumns = useMemo(() => {
    const columns: Column<Brand>[] = [];
    
    columns.push({
      id: 'name',
      header: 'Name',
      cell: (brand) => editingBrand?.id === brand.id ? (
        <Input
          value={editingBrand?.name ?? ""}
          onChange={(e) => setEditingBrand({...editingBrand!, name: e.target.value})}
          disabled={!canManage}
        />
      ) : (
        brand.name
      ),
      sortable: true,
      minWidth: '200px'
    });

    columns.push({
      id: 'brandSize',
      header: 'Type',
      cell: (brand) => editingBrand?.id === brand.id ? (
        <Select
          value={editingBrand?.brandSize}
          onValueChange={(value) => setEditingBrand({...editingBrand!, brandSize: value as 'craft' | 'mass' | 'unknown'})}
          disabled={!canManage}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="craft">Craft</SelectItem>
            <SelectItem value="mass">Commercial</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
      ) : (
        brand.brandSize === "craft" ? "Craft" : 
        brand.brandSize === "mass" ? "Commercial" : "Unknown"
      ),
      sortable: true,
      minWidth: '150px'
    });

    if (canManage) {
      columns.push({
        id: 'actions',
        header: 'Actions',
        cell: (brand) => editingBrand?.id === brand.id ? (
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={handleEditBrand} disabled={!canManage}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditingBrand(null)}>Cancel</Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <Button 
              size="sm"
              variant="outline" 
              onClick={() => setEditingBrand(brand)}
              disabled={!canManage}
            >
              Edit
            </Button>
            <Button 
              size="sm"
              variant="destructive" 
              onClick={() => handleDeleteBrand(brand)}
              disabled={!canManage || !brand.id}
            >
              Delete
            </Button>
          </div>
        ),
        sortable: false,
        minWidth: '180px'
      });
    }
    
    return columns;
  }, [editingBrand, canManage, handleEditBrand, handleDeleteBrand]);
  
  // Create sort config for the table
  const tableSortConfig: SortConfig<Brand> = useMemo(() => ({
    column: sortColumn as keyof Brand | null,
    direction: sortDirection
  }), [sortColumn, sortDirection]);
  
  // Empty message with filters applied
  const emptyMessage = useMemo(() => {
    let message = "No brands found";
    if (searchTerm) message += ` for "${searchTerm}"`;
    if (filterBrandSize !== null) message += ` of type "${filterBrandSize}"`;
    return message + '.';
  }, [searchTerm, filterBrandSize]);

  return (
    <>
      <Card className="h-full flex flex-col overflow-hidden">
        <CardHeader>
          <CardTitle>Soda Brands ({brands?.length || 0})</CardTitle>
          <CardDescription>Manage your soda brands here</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col overflow-hidden pb-6">
          <div className="flex justify-between items-start mb-4 gap-4">
            <div className="flex gap-2 items-end">
              <div>
                <Label htmlFor="brandSizeFilter">Filter by Type</Label>
                <Select 
                  value={filterBrandSize === null ? "all" : filterBrandSize} 
                  onValueChange={(value) => {
                    setFilterBrandSize(value === "all" ? null : value);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Brands</SelectItem>
                    <SelectItem value="craft">Craft Brands</SelectItem>
                    <SelectItem value="mass">Commercial Brands</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="searchTerm">Filter by Name</Label>
                <Input
                  id="searchTerm"
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-[200px]"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              {canManage && (
                <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                  <DialogTrigger asChild>
                    <Button disabled={!canManage}>Add New Brand</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Brand</DialogTitle>
                      <DialogDescription>
                        Fill in the details for the new soda brand.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="grid gap-4 py-4">
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="name" className="text-right">Name</Label>
                        <Input 
                          id="name" 
                          value={newBrand.name} 
                          onChange={(e) => setNewBrand({...newBrand, name: e.target.value})}
                          className="col-span-3"
                          disabled={!canManage}
                        />
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="brandSize" className="text-right">Brand Type</Label>
                        <Select
                          value={newBrand.brandSize}
                          onValueChange={(value) => setNewBrand({...newBrand, brandSize: value as 'craft' | 'mass' | 'unknown'})}
                          disabled={!canManage}
                        >
                          <SelectTrigger className="col-span-3">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="craft">Craft</SelectItem>
                            <SelectItem value="mass">Commercial</SelectItem>
                            <SelectItem value="unknown">Unknown</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button onClick={handleAddBrand} disabled={!canManage}>Add Brand</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
          
          {generalLoading && !processedBrands?.length ? (
            <div className="flex-1 flex items-center justify-center">Loading brands...</div>
          ) : (
            <ScrollableTable
              data={processedBrands}
              columns={tableColumns}
              sortConfig={tableSortConfig}
              onSort={handleSort}
              emptyMessage={emptyMessage}
              height="calc(100vh - 370px)"
            />
          )}
        </CardContent>
      </Card>

      {/* Deletion Dialog */}
      {(interactionState === 'confirming_deletion' || interactionState === 'assessing_deletion') && selectedEntity?.type === 'brand' && (
        <DeleteDialog
          isOpen={true}
          onClose={handleDeletionDialogClose}
          entityType={selectedEntity.type as EditableEntityType}
          entityName={deletionImpact.report?.primaryDocument.name || selectedEntity.id || 'Brand'}
          assessmentState={getDeletionAssessmentState()}
          impactReport={deletionImpact.report}
          deletionState={operationLoading === 'pending' ? 'loading' : operationError ? 'error' : 'idle'}
          error={deletionImpact.error || operationError}
          onConfirm={handleConfirmBrandDeletion}
        />
      )}
    </>
  );
}; 